import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const PALETTE = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#06b6d4", "#f97316", "#84cc16",
];

function fmt(value: unknown): string {
  if (typeof value !== "number") return value == null ? "" : String(value);
  return value.toLocaleString("en-GB", { maximumFractionDigits: 2 });
}

interface TooltipEntry {
  dataKey?: string | number;
  color?: string;
  name?: string | number;
  value?: number;
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipEntry[]; label?: string | number }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      {label && <p className="mb-1 font-semibold text-gray-700">{label}</p>}
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
}

interface ChartYKey {
  key: string;
  label?: string;
  color?: string;
}

interface ChartSpec {
  type?: string;
  title?: string;
  data?: Array<Record<string, unknown>>;
  xKey?: string;
  yKeys?: ChartYKey[];
  nameKey?: string;
  valueKey?: string;
}

export default function ChartBlock({ spec }: { spec: ChartSpec }) {
  const { type, title, data, xKey, yKeys = [], nameKey, valueKey } = spec;

  if (!data?.length) return null;

  const commonProps = {
    data,
    margin: { top: 4, right: 16, left: 0, bottom: 4 },
  };

  let chart;

  if (type === "pie") {
    chart = (
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey || "value"}
          nameKey={nameKey || "name"}
          cx="50%"
          cy="50%"
          outerRadius={100}
          label={({ name, percent }: { name?: string; percent?: number }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Pie>
        <Tooltip formatter={fmt} />
        <Legend />
      </PieChart>
    );
  } else if (type === "line") {
    chart = (
      <LineChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} width={60} />
        <Tooltip content={<CustomTooltip />} />
        {yKeys.length > 1 && <Legend />}
        {yKeys.map((y, i) => (
          <Line key={y.key} type="monotone" dataKey={y.key} name={y.label || y.key}
            stroke={y.color || PALETTE[i % PALETTE.length]} dot={false} strokeWidth={2} />
        ))}
      </LineChart>
    );
  } else if (type === "area") {
    chart = (
      <AreaChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} width={60} />
        <Tooltip content={<CustomTooltip />} />
        {yKeys.length > 1 && <Legend />}
        {yKeys.map((y, i) => (
          <Area key={y.key} type="monotone" dataKey={y.key} name={y.label || y.key}
            stroke={y.color || PALETTE[i % PALETTE.length]}
            fill={y.color || PALETTE[i % PALETTE.length]}
            fillOpacity={0.15} strokeWidth={2} />
        ))}
      </AreaChart>
    );
  } else {
    chart = (
      <BarChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} width={60} />
        <Tooltip content={<CustomTooltip />} />
        {yKeys.length > 1 && <Legend />}
        {yKeys.map((y, i) => (
          <Bar key={y.key} dataKey={y.key} name={y.label || y.key}
            fill={y.color || PALETTE[i % PALETTE.length]} radius={[3, 3, 0, 0]} />
        ))}
      </BarChart>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 px-4 pb-3 pt-3">
      {title && <p className="mb-3 text-xs font-semibold text-gray-600">{title}</p>}
      <ResponsiveContainer width="100%" height={260}>
        {chart}
      </ResponsiveContainer>
    </div>
  );
}
